import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DataTablePage from "./DataTablePage";
import ProductImage from "../../components/ProductImage";
import ProductEditDialog from "../ProductEditDialog";
import { ProductVM } from "../../data/types";
import { toProductVM } from "../../data/mappers";
import { formatCAD } from "../../utils/currency";
import { foodImageDataUri } from "../../utils/foodImage";
import { fetchProducts } from "../../data/api";

const ProductsPage: React.FC = () => {
  const [raw, setRaw] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<any | null | undefined>(undefined); // undefined=closed, null=new

  const load = useCallback(async () => {
    setRaw((await fetchProducts()) || []);
  }, []);

  // Reflect an edit/upload immediately (optimistic), then reconcile with server.
  const applySaved = useCallback((saved?: any) => {
    if (saved && saved.id != null) {
      setRaw((cur) => {
        const i = cur.findIndex((p) => Number(p?.id) === Number(saved.id));
        if (i >= 0) { const next = [...cur]; next[i] = { ...next[i], ...saved }; return next; }
        return [saved, ...cur];
      });
    }
    load();
  }, [load]);

  useEffect(() => { (async () => { try { await load(); } finally { setLoading(false); } })(); }, [load]);

  const rawById = useMemo(() => {
    const m = new Map<number, any>();
    raw.forEach((p) => m.set(Number(p?.id), p));
    return m;
  }, [raw]);

  const rows = useMemo(() => raw.map(toProductVM), [raw]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => r.name.toLowerCase().includes(s) || (r.sku || "").toLowerCase().includes(s) || (r.category || "").toLowerCase().includes(s)) : rows;
  }, [rows, q]);

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(null)}>New product</Button>
      </Stack>
      <DataTablePage<ProductVM>
        title="Products" subtitle={`${rows.length} products in your catalogue`}
        search={q} onSearch={setQ} searchPlaceholder="Search name, SKU or category"
        loading={loading} rows={filtered} empty="No products yet. Use “New product” to add one."
        onRowClick={(r) => setEditing(rawById.get(r.id) ?? null)}
        columns={[
          { header: "", render: (r) => <Box sx={{ width: 44 }}><ProductImage src={r.image} fallback={foodImageDataUri(r.name, r.category)} alt={r.name} /></Box> },
          { header: "Product", render: (r) => <Typography variant="body2" fontWeight={600}>{r.name}</Typography> },
          { header: "SKU", render: (r) => r.sku },
          { header: "Category", render: (r) => r.category || "—" },
          { header: "Packaging", render: (r) => r.packagingUnit },
          { header: "Price", render: (r) => formatCAD(r.price) },
          { header: "Stock", render: (r) => r.stock },
          { header: "Status", render: (r) => <Chip size="small" label={r.stock > 0 ? "In Stock" : "Out of Stock"}
              sx={{ bgcolor: r.stock > 0 ? "#e7f3ec" : "#fde8e8", color: r.stock > 0 ? "#1f7a4d" : "#c0392b", fontWeight: 600 }} /> },
          { header: "", render: (r) => <Button size="small" onClick={(e) => { e.stopPropagation(); setEditing(rawById.get(r.id) ?? null); }}>Edit</Button> },
        ]}
      />
      <ProductEditDialog
        open={editing !== undefined}
        product={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={applySaved}
      />
    </>
  );
};
export default ProductsPage;
